import type {
  ComponentBaseMapper,
  TriggerRenderer,
  EventStateRegistry,
  CustomFieldRenderer,
  TriggerRendererContext,
  TriggerEventContext,
} from "./types";
import type {
  SuperplaneComponentsNode as ComponentsNode,
  CanvasesCanvasNodeExecution,
  CanvasesCanvasRunRef,
} from "@/api-client";
import { defaultTriggerRenderer } from "./default";
import { scheduleTriggerRenderer, scheduleCustomFieldRenderer } from "./schedule";
import { webhookTriggerRenderer, webhookCustomFieldRenderer } from "./webhook";
import { noopMapper } from "./noop";
import { displayMapper } from "./display";
import { addMemoryMapper } from "./addMemory";
import { deleteMemoryMapper } from "./deleteMemory";
import { readMemoryMapper } from "./readMemory";
import { updateMemoryMapper } from "./updateMemory";
import { upsertMemoryMapper } from "./upsertMemory";
import { ifMapper, IF_STATE_REGISTRY } from "./if";
import { httpMapper, HTTP_STATE_REGISTRY } from "./http";
import { graphqlMapper, GRAPHQL_STATE_REGISTRY } from "./graphql";
import {
  componentMappers as semaphoreComponentMappers,
  triggerRenderers as semaphoreTriggerRenderers,
  eventStateRegistry as semaphoreEventStateRegistry,
} from "./semaphore/index";
import {
  componentMappers as githubComponentMappers,
  triggerRenderers as githubTriggerRenderers,
  eventStateRegistry as githubEventStateRegistry,
} from "./github/index";
import {
  componentMappers as gitlabComponentMappers,
  triggerRenderers as gitlabTriggerRenderers,
  eventStateRegistry as gitlabEventStateRegistry,
} from "./gitlab/index";
import {
  componentMappers as jiraComponentMappers,
  triggerRenderers as jiraTriggerRenderers,
  eventStateRegistry as jiraEventStateRegistry,
} from "./jira/index";
import {
  componentMappers as linearComponentMappers,
  triggerRenderers as linearTriggerRenderers,
  eventStateRegistry as linearEventStateRegistry,
} from "./linear/index";
import {
  componentMappers as pagerdutyComponentMappers,
  triggerRenderers as pagerdutyTriggerRenderers,
  eventStateRegistry as pagerdutyEventStateRegistry,
} from "./pagerduty/index";
import {
  componentMappers as productiveComponentMappers,
  triggerRenderers as productiveTriggerRenderers,
  eventStateRegistry as productiveEventStateRegistry,
} from "./productive/index";
import {
  componentMappers as dash0ComponentMappers,
  triggerRenderers as dash0TriggerRenderers,
  eventStateRegistry as dash0EventStateRegistry,
} from "./dash0/index";
import {
  componentMappers as daytonaComponentMappers,
  triggerRenderers as daytonaTriggerRenderers,
  eventStateRegistry as daytonaEventStateRegistry,
} from "./daytona/index";
import {
  componentMappers as cloudflareComponentMappers,
  triggerRenderers as cloudflareTriggerRenderers,
  eventStateRegistry as cloudflareEventStateRegistry,
} from "./cloudflare/index";
import {
  componentMappers as cloudsmithComponentMappers,
  triggerRenderers as cloudsmithTriggerRenderers,
  eventStateRegistry as cloudsmithEventStateRegistry,
} from "./cloudsmith/index";
import {
  componentMappers as datadogComponentMappers,
  triggerRenderers as datadogTriggerRenderers,
  eventStateRegistry as datadogEventStateRegistry,
} from "./datadog/index";
import {
  componentMappers as slackComponentMappers,
  triggerRenderers as slackTriggerRenderers,
  eventStateRegistry as slackEventStateRegistry,
} from "./slack";
import {
  componentMappers as smtpComponentMappers,
  triggerRenderers as smtpTriggerRenderers,
  eventStateRegistry as smtpEventStateRegistry,
} from "./smtp";
import {
  componentMappers as sendgridComponentMappers,
  triggerRenderers as sendgridTriggerRenderers,
  eventStateRegistry as sendgridEventStateRegistry,
} from "./sendgrid";
import {
  componentMappers as sentryComponentMappers,
  triggerRenderers as sentryTriggerRenderers,
  eventStateRegistry as sentryEventStateRegistry,
} from "./sentry/index";
import {
  componentMappers as renderComponentMappers,
  triggerRenderers as renderTriggerRenderers,
  eventStateRegistry as renderEventStateRegistry,
} from "./render";
import {
  componentMappers as rootlyComponentMappers,
  triggerRenderers as rootlyTriggerRenderers,
  eventStateRegistry as rootlyEventStateRegistry,
} from "./rootly/index";
import {
  componentMappers as incidentComponentMappers,
  triggerRenderers as incidentTriggerRenderers,
  eventStateRegistry as incidentEventStateRegistry,
  customFieldRenderers as incidentCustomFieldRenderers,
} from "./incident/index";
import {
  componentMappers as newrelicComponentMappers,
  customFieldRenderers as newrelicCustomFieldRenderers,
  triggerRenderers as newrelicTriggerRenderers,
  eventStateRegistry as newrelicEventStateRegistry,
} from "./newrelic/index";
import {
  componentMappers as firehydrantComponentMappers,
  triggerRenderers as firehydrantTriggerRenderers,
  eventStateRegistry as firehydrantEventStateRegistry,
} from "./firehydrant/index";
import {
  componentMappers as launchdarklyComponentMappers,
  triggerRenderers as launchdarklyTriggerRenderers,
  eventStateRegistry as launchdarklyEventStateRegistry,
} from "./launchdarkly/index";
import {
  componentMappers as awsComponentMappers,
  triggerRenderers as awsTriggerRenderers,
  eventStateRegistry as awsEventStateRegistry,
} from "./aws";
import { triggerRenderers as bitbucketTriggerRenderers } from "./bitbucket/index";
import { componentMappers as coolifyComponentMappers } from "./coolify/index";
import { componentMappers as hetznerComponentMappers } from "./hetzner/index";
import {
  componentMappers as jfrogArtifactoryComponentMappers,
  triggerRenderers as jfrogArtifactoryTriggerRenderers,
  eventStateRegistry as jfrogArtifactoryEventStateRegistry,
} from "./jfrogArtifactory/index";
import {
  componentMappers as azureComponentMappers,
  triggerRenderers as azureTriggerRenderers,
  eventStateRegistry as azureEventStateRegistry,
} from "./azure/index";
import { timeGateMapper, TIME_GATE_STATE_REGISTRY } from "./timegate";
import {
  componentMappers as digitaloceanComponentMappers,
  triggerRenderers as digitaloceanTriggerRenderers,
  eventStateRegistry as digitaloceanEventStateRegistry,
} from "./digitalocean/index";
import {
  componentMappers as discordComponentMappers,
  triggerRenderers as discordTriggerRenderers,
  eventStateRegistry as discordEventStateRegistry,
} from "./discord";
import {
  componentMappers as telegramComponentMappers,
  triggerRenderers as telegramTriggerRenderers,
  eventStateRegistry as telegramEventStateRegistry,
} from "./telegram";
import {
  componentMappers as octopusComponentMappers,
  triggerRenderers as octopusTriggerRenderers,
  eventStateRegistry as octopusEventStateRegistry,
} from "./octopus/index";
import {
  componentMappers as teamsComponentMappers,
  triggerRenderers as teamsTriggerRenderers,
  eventStateRegistry as teamsEventStateRegistry,
} from "./teams";
import {
  componentMappers as openaiComponentMappers,
  triggerRenderers as openaiTriggerRenderers,
  eventStateRegistry as openaiEventStateRegistry,
} from "./openai/index";
import {
  componentMappers as grafanaComponentMappers,
  customFieldRenderers as grafanaCustomFieldRenderers,
  triggerRenderers as grafanaTriggerRenderers,
  eventStateRegistry as grafanaEventStateRegistry,
} from "./grafana/index";
import {
  componentMappers as circleCIComponentMappers,
  triggerRenderers as circleCITriggerRenderers,
  eventStateRegistry as circleCIEventStateRegistry,
} from "./circleci/index";
import {
  componentMappers as harnessComponentMappers,
  triggerRenderers as harnessTriggerRenderers,
  eventStateRegistry as harnessEventStateRegistry,
} from "./harness";
import {
  componentMappers as claudeComponentMappers,
  triggerRenderers as claudeTriggerRenderers,
  eventStateRegistry as claudeEventStateRegistry,
} from "./claude/index";
import {
  componentMappers as logfireComponentMappers,
  triggerRenderers as logfireTriggerRenderers,
  eventStateRegistry as logfireEventStateRegistry,
} from "./logfire/index";
import {
  componentMappers as perplexityComponentMappers,
  triggerRenderers as perplexityTriggerRenderers,
  eventStateRegistry as perplexityEventStateRegistry,
} from "./perplexity/index";
import {
  componentMappers as openrouterComponentMappers,
  triggerRenderers as openrouterTriggerRenderers,
  eventStateRegistry as openrouterEventStateRegistry,
} from "./openrouter/index";
import {
  componentMappers as prometheusComponentMappers,
  customFieldRenderers as prometheusCustomFieldRenderers,
  triggerRenderers as prometheusTriggerRenderers,
  eventStateRegistry as prometheusEventStateRegistry,
} from "./prometheus/index";
import {
  componentMappers as cursorComponentMappers,
  triggerRenderers as cursorTriggerRenderers,
  eventStateRegistry as cursorEventStateRegistry,
} from "./cursor/index";
import {
  componentMappers as statuspageComponentMappers,
  triggerRenderers as statuspageTriggerRenderers,
  eventStateRegistry as statuspageEventStateRegistry,
} from "./statuspage";
import {
  componentMappers as dockerhubComponentMappers,
  customFieldRenderers as dockerhubCustomFieldRenderers,
  triggerRenderers as dockerhubTriggerRenderers,
  eventStateRegistry as dockerhubEventStateRegistry,
} from "./dockerhub";

import {
  componentMappers as honeycombComponentMappers,
  triggerRenderers as honeycombTriggerRenderers,
  eventStateRegistry as honeycombEventStateRegistry,
} from "./honeycomb/index";
import {
  componentMappers as gcpComponentMappers,
  customFieldRenderers as gcpCustomFieldRenderers,
  triggerRenderers as gcpTriggerRenderers,
  eventStateRegistry as gcpEventStateRegistry,
} from "./gcp";
import {
  componentMappers as servicenowComponentMappers,
  customFieldRenderers as servicenowCustomFieldRenderers,
  triggerRenderers as servicenowTriggerRenderers,
  eventStateRegistry as servicenowEventStateRegistry,
} from "./servicenow/index";
import {
  componentMappers as elasticComponentMappers,
  triggerRenderers as elasticTriggerRenderers,
  eventStateRegistry as elasticEventStateRegistry,
} from "./elastic/index";
import {
  componentMappers as ociComponentMappers,
  triggerRenderers as ociTriggerRenderers,
  eventStateRegistry as ociEventStateRegistry,
} from "./oci/index";

import { filterMapper, FILTER_STATE_REGISTRY } from "./filter";
import { forEachMapper, FOR_EACH_STATE_REGISTRY } from "./forEach";
import { sshMapper, SSH_STATE_REGISTRY } from "./ssh";
import { agentHarnessMapper, runnerMapper, RUNNER_STATE_REGISTRY } from "./runner";
import { waitCustomFieldRenderer, waitMapper, WAIT_STATE_REGISTRY } from "./wait";
import { approvalMapper, APPROVAL_STATE_REGISTRY } from "./approval";
import { loopMapper, LOOP_STATE_REGISTRY } from "./loop";
import { addRunErrorMapper } from "./addRunError";
import { runAppMapper, RUN_APP_STATE_REGISTRY } from "./runApp";
import { mergeMapper, MERGE_STATE_REGISTRY } from "./merge";
import { DEFAULT_STATE_REGISTRY } from "./stateRegistry";
import { startTriggerRenderer } from "./start";
import { onBroadcastTriggerRenderer } from "./messages/on_broadcast";
import { onRunTriggerRenderer } from "./messages/on_run";
import { buildExecutionInfo, buildNodeInfo } from "../utils";
import { createSafeComponentMapper, createSafeCustomFieldRenderer, createSafeTriggerRenderer } from "./safeMappers";
import { registerMapperLookups } from "./mapperLookup";

/**
 * Registry mapping trigger names to their renderers.
 * Any trigger type not in this registry will use the defaultTriggerRenderer.
 */
const triggerRenderers: Record<string, TriggerRenderer> = {
  schedule: scheduleTriggerRenderer,
  webhook: webhookTriggerRenderer,
  start: startTriggerRenderer,
  onBroadcast: onBroadcastTriggerRenderer,
  onRun: onRunTriggerRenderer,
};

const componentBaseMappers: Record<string, ComponentBaseMapper> = {
  noop: noopMapper,
  display: displayMapper,
  addMemory: addMemoryMapper,
  deleteMemory: deleteMemoryMapper,
  readMemory: readMemoryMapper,
  updateMemory: updateMemoryMapper,
  upsertMemory: upsertMemoryMapper,
  if: ifMapper,
  loop: loopMapper,
  http: httpMapper,
  graphql: graphqlMapper,
  ssh: sshMapper,
  runner: runnerMapper,
  runnerJS: runnerMapper,
  runnerBash: runnerMapper,
  runnerPython: runnerMapper,
  runnerSuperPlane: agentHarnessMapper,
  runnerClaudeCode: agentHarnessMapper,
  runnerCodex: agentHarnessMapper,
  runnerOpenRouter: agentHarnessMapper,
  timeGate: timeGateMapper,
  filter: filterMapper,
  forEach: forEachMapper,
  wait: waitMapper,
  approval: approvalMapper,
  merge: mergeMapper,
  runApp: runAppMapper,
  addRunError: addRunErrorMapper,
};

const appMappers: Record<string, Record<string, ComponentBaseMapper>> = {
  cloudflare: cloudflareComponentMappers,
  cloudsmith: cloudsmithComponentMappers,
  digitalocean: digitaloceanComponentMappers,
  semaphore: semaphoreComponentMappers,
  github: githubComponentMappers,
  gitlab: gitlabComponentMappers,
  jira: jiraComponentMappers,
  linear: linearComponentMappers,
  grafana: grafanaComponentMappers,
  pagerduty: pagerdutyComponentMappers,
  productive: productiveComponentMappers,
  dash0: dash0ComponentMappers,
  daytona: daytonaComponentMappers,
  datadog: datadogComponentMappers,
  slack: slackComponentMappers,
  smtp: smtpComponentMappers,
  sendgrid: sendgridComponentMappers,
  sentry: sentryComponentMappers,
  render: renderComponentMappers,
  rootly: rootlyComponentMappers,
  incident: incidentComponentMappers,
  newrelic: newrelicComponentMappers,
  firehydrant: firehydrantComponentMappers,
  launchdarkly: launchdarklyComponentMappers,
  aws: awsComponentMappers,
  azure: azureComponentMappers,
  discord: discordComponentMappers,
  telegram: telegramComponentMappers,
  octopus: octopusComponentMappers,
  teams: teamsComponentMappers,
  openai: openaiComponentMappers,
  circleci: circleCIComponentMappers,
  claude: claudeComponentMappers,
  logfire: logfireComponentMappers,
  perplexity: perplexityComponentMappers,
  openrouter: openrouterComponentMappers,
  gcp: gcpComponentMappers,
  prometheus: prometheusComponentMappers,
  cursor: cursorComponentMappers,
  coolify: coolifyComponentMappers,
  hetzner: hetznerComponentMappers,
  jfrogArtifactory: jfrogArtifactoryComponentMappers,
  statuspage: statuspageComponentMappers,
  dockerhub: dockerhubComponentMappers,
  honeycomb: honeycombComponentMappers,
  harness: harnessComponentMappers,
  servicenow: servicenowComponentMappers,
  elastic: elasticComponentMappers,
  oci: ociComponentMappers,
};

const appTriggerRenderers: Record<string, Record<string, TriggerRenderer>> = {
  cloudflare: cloudflareTriggerRenderers,
  cloudsmith: cloudsmithTriggerRenderers,
  digitalocean: digitaloceanTriggerRenderers,
  semaphore: semaphoreTriggerRenderers,
  github: githubTriggerRenderers,
  gitlab: gitlabTriggerRenderers,
  jira: jiraTriggerRenderers,
  linear: linearTriggerRenderers,
  pagerduty: pagerdutyTriggerRenderers,
  productive: productiveTriggerRenderers,
  dash0: dash0TriggerRenderers,
  daytona: daytonaTriggerRenderers,
  datadog: datadogTriggerRenderers,
  slack: slackTriggerRenderers,
  smtp: smtpTriggerRenderers,
  sendgrid: sendgridTriggerRenderers,
  sentry: sentryTriggerRenderers,
  render: renderTriggerRenderers,
  rootly: rootlyTriggerRenderers,
  incident: incidentTriggerRenderers,
  newrelic: newrelicTriggerRenderers,
  firehydrant: firehydrantTriggerRenderers,
  launchdarkly: launchdarklyTriggerRenderers,
  aws: awsTriggerRenderers,
  azure: azureTriggerRenderers,
  discord: discordTriggerRenderers,
  telegram: telegramTriggerRenderers,
  octopus: octopusTriggerRenderers,
  teams: teamsTriggerRenderers,
  openai: openaiTriggerRenderers,
  circleci: circleCITriggerRenderers,
  claude: claudeTriggerRenderers,
  logfire: logfireTriggerRenderers,
  perplexity: perplexityTriggerRenderers,
  openrouter: openrouterTriggerRenderers,
  gcp: gcpTriggerRenderers,
  grafana: grafanaTriggerRenderers,
  bitbucket: bitbucketTriggerRenderers,
  prometheus: prometheusTriggerRenderers,
  cursor: cursorTriggerRenderers,
  jfrogArtifactory: jfrogArtifactoryTriggerRenderers,
  statuspage: statuspageTriggerRenderers,
  dockerhub: dockerhubTriggerRenderers,
  honeycomb: honeycombTriggerRenderers,
  harness: harnessTriggerRenderers,
  servicenow: servicenowTriggerRenderers,
  elastic: elasticTriggerRenderers,
  oci: ociTriggerRenderers,
};

const appEventStateRegistries: Record<string, Record<string, EventStateRegistry>> = {
  cloudflare: cloudflareEventStateRegistry,
  cloudsmith: cloudsmithEventStateRegistry,
  digitalocean: digitaloceanEventStateRegistry,
  semaphore: semaphoreEventStateRegistry,
  github: githubEventStateRegistry,
  pagerduty: pagerdutyEventStateRegistry,
  productive: productiveEventStateRegistry,
  dash0: dash0EventStateRegistry,
  daytona: daytonaEventStateRegistry,
  datadog: datadogEventStateRegistry,
  slack: slackEventStateRegistry,
  smtp: smtpEventStateRegistry,
  sendgrid: sendgridEventStateRegistry,
  sentry: sentryEventStateRegistry,
  render: renderEventStateRegistry,
  discord: discordEventStateRegistry,
  telegram: telegramEventStateRegistry,
  teams: teamsEventStateRegistry,
  rootly: rootlyEventStateRegistry,
  incident: incidentEventStateRegistry,
  newrelic: newrelicEventStateRegistry,
  octopus: octopusEventStateRegistry,
  firehydrant: firehydrantEventStateRegistry,
  launchdarkly: launchdarklyEventStateRegistry,
  openai: openaiEventStateRegistry,
  circleci: circleCIEventStateRegistry,
  claude: claudeEventStateRegistry,
  logfire: logfireEventStateRegistry,
  perplexity: perplexityEventStateRegistry,
  openrouter: openrouterEventStateRegistry,
  gcp: gcpEventStateRegistry,
  statuspage: statuspageEventStateRegistry,
  aws: awsEventStateRegistry,
  grafana: grafanaEventStateRegistry,
  prometheus: prometheusEventStateRegistry,
  cursor: cursorEventStateRegistry,
  azure: azureEventStateRegistry,
  gitlab: gitlabEventStateRegistry,
  jira: jiraEventStateRegistry,
  linear: linearEventStateRegistry,
  jfrogArtifactory: jfrogArtifactoryEventStateRegistry,
  dockerhub: dockerhubEventStateRegistry,
  honeycomb: honeycombEventStateRegistry,
  harness: harnessEventStateRegistry,
  servicenow: servicenowEventStateRegistry,
  elastic: elasticEventStateRegistry,
  oci: ociEventStateRegistry,
};

const eventStateRegistries: Record<string, EventStateRegistry> = {
  approval: APPROVAL_STATE_REGISTRY,
  http: HTTP_STATE_REGISTRY,
  graphql: GRAPHQL_STATE_REGISTRY,
  ssh: SSH_STATE_REGISTRY,
  runner: RUNNER_STATE_REGISTRY,
  runnerJS: RUNNER_STATE_REGISTRY,
  runnerBash: RUNNER_STATE_REGISTRY,
  runnerPython: RUNNER_STATE_REGISTRY,
  runnerSuperPlane: RUNNER_STATE_REGISTRY,
  runnerClaudeCode: RUNNER_STATE_REGISTRY,
  runnerCodex: RUNNER_STATE_REGISTRY,
  runnerOpenRouter: RUNNER_STATE_REGISTRY,
  filter: FILTER_STATE_REGISTRY,
  forEach: FOR_EACH_STATE_REGISTRY,
  if: IF_STATE_REGISTRY,
  loop: LOOP_STATE_REGISTRY,
  timeGate: TIME_GATE_STATE_REGISTRY,
  wait: WAIT_STATE_REGISTRY,
  merge: MERGE_STATE_REGISTRY,
  runApp: RUN_APP_STATE_REGISTRY,
};

const customFieldRenderers: Record<string, CustomFieldRenderer> = {
  schedule: scheduleCustomFieldRenderer,
  wait: waitCustomFieldRenderer,
  webhook: webhookCustomFieldRenderer,
};

const appCustomFieldRenderers: Record<string, Record<string, CustomFieldRenderer>> = {
  grafana: grafanaCustomFieldRenderers,
  newrelic: newrelicCustomFieldRenderers,
  prometheus: prometheusCustomFieldRenderers,
  dockerhub: dockerhubCustomFieldRenderers,
  incident: incidentCustomFieldRenderers,
  gcp: gcpCustomFieldRenderers,
  servicenow: servicenowCustomFieldRenderers,
};

/**
 * Get the appropriate renderer for a trigger type.
 * Falls back to the default renderer if no specific renderer is registered.
 * The returned renderer is wrapped in a safe wrapper that catches exceptions
 * to prevent a single trigger mapper failure from breaking the entire canvas.
 */
export function getTriggerRenderer(name: string): TriggerRenderer {
  if (!name) {
    return createSafeTriggerRenderer(defaultTriggerRenderer, name || "default");
  }

  const parts = name?.split(".");
  if (parts?.length === 1) {
    return createSafeTriggerRenderer(withCustomName(triggerRenderers[name] || defaultTriggerRenderer), name);
  }

  const appName = parts[0];
  const appTriggers = appTriggerRenderers[appName];
  if (!appTriggers) {
    return createSafeTriggerRenderer(withCustomName(defaultTriggerRenderer), name);
  }

  const triggerName = parts.slice(1).join(".");
  return createSafeTriggerRenderer(withCustomName(appTriggers[triggerName] || defaultTriggerRenderer), name);
}

/**
 * Get the appropriate mapper for a component.
 * Falls back to the noop mapper if no specific mapper is registered.
 * The returned mapper is wrapped in a safe wrapper that catches exceptions
 * to prevent a single component mapper failure from breaking the entire canvas.
 */
export function getComponentBaseMapper(name: string): ComponentBaseMapper {
  return createSafeComponentMapper(findRegisteredComponentMapper(name) || noopMapper, name || "noop");
}

/**
 * Get the appropriate state registry for a component type.
 * Falls back to the default state registry if no specific registry is registered.
 */
export function getEventStateRegistry(name: string): EventStateRegistry {
  const direct = eventStateRegistries[name];
  if (direct) {
    return direct;
  }

  const parts = name.split(".");
  if (parts.length === 1) {
    return DEFAULT_STATE_REGISTRY;
  }

  const appName = parts[0];
  const appRegistry = appEventStateRegistries[appName];
  if (!appRegistry) {
    return DEFAULT_STATE_REGISTRY;
  }

  const componentName = parts.slice(1).join(".");
  return appRegistry[componentName] || DEFAULT_STATE_REGISTRY;
}

/**
 * Get the state map for a component type.
 * Falls back to the default state map if no specific registry is registered.
 */
export function getStateMap(componentName: string) {
  return getEventStateRegistry(componentName).stateMap;
}

/**
 * Get the state function for a component type.
 * Falls back to the default state function if no specific registry is registered.
 */
export function getState(componentName: string) {
  return getEventStateRegistry(componentName).getState;
}

/**
 * Get the appropriate custom field renderer for a component/trigger type.
 * Returns undefined if no specific renderer is registered.
 */
export function getCustomFieldRenderer(componentName: string): CustomFieldRenderer | undefined {
  const parts = componentName?.split(".");
  if (parts?.length === 1) {
    const renderer = customFieldRenderers[componentName];
    return renderer ? createSafeCustomFieldRenderer(renderer, componentName) : undefined;
  }

  const appName = parts[0];
  const appRenderers = appCustomFieldRenderers[appName];
  if (!appRenderers) {
    return undefined;
  }

  const name = parts.slice(1).join(".");
  const renderer = appRenderers[name];
  return renderer ? createSafeCustomFieldRenderer(renderer, componentName) : undefined;
}

/**
 * Get the execution details for a component execution.
 * Returns undefined if no specific execution details function is registered.
 * Catches exceptions from mappers to prevent canvas-wide failures.
 */
export function getExecutionDetails(
  componentName: string,
  execution: CanvasesCanvasNodeExecution,
  node: ComponentsNode,
  nodes?: ComponentsNode[],
  childRuns?: CanvasesCanvasRunRef[],
): Record<string, unknown> | undefined {
  const mapper = findRegisteredComponentMapper(componentName);
  if (!mapper) {
    return undefined;
  }

  return createSafeComponentMapper(mapper, componentName).getExecutionDetails({
    execution: buildExecutionInfo(execution, { runs: childRuns }),
    node: buildNodeInfo(node),
    nodes: nodes?.map((n) => buildNodeInfo(n)) || [],
  });
}

function findRegisteredComponentMapper(name: string): ComponentBaseMapper | undefined {
  if (!name) {
    return undefined;
  }

  const direct = componentBaseMappers[name];
  if (direct) {
    return direct;
  }

  const parts = name.split(".");
  if (parts.length === 1) {
    return undefined;
  }

  const appMapper = appMappers[parts[0]];
  if (!appMapper) {
    return undefined;
  }

  return appMapper[parts.slice(1).join(".")];
}

function withCustomName(renderer: TriggerRenderer): TriggerRenderer {
  return {
    ...renderer,
    getTriggerProps: (context: TriggerRendererContext) => {
      const props = renderer.getTriggerProps(context);
      const customName = context.lastEvent?.customName?.trim();
      if (customName && props.lastEventData) {
        return {
          ...props,
          lastEventData: {
            ...props.lastEventData,
            title: customName,
          },
        };
      }

      return props;
    },
    getTitleAndSubtitle: (context: TriggerEventContext) => {
      const { title, subtitle } = renderer.getTitleAndSubtitle(context);
      const customName = context.event?.customName?.trim();
      if (customName) {
        return { title: customName, subtitle };
      }

      return { title, subtitle };
    },
  };
}

registerMapperLookups({
  getState,
  getStateMap,
  getTriggerRenderer,
});
